import React, { FC, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import isEqual from 'lodash.isequal';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import axios from 'axios';

import { createEnterpriseAccountValidation } from '@constants';
import { queryClient } from '@helpers';
import { useConfirmationModal, useModalState } from '@hooks';
import { CompanyType } from '@services';

import { ConfirmModal } from '@organisms/Modals';
import MainModal from './MainModal';
import SuccessModal from './SuccessModal';
import { usePresignedUrl } from '@hooks/usePresignedUrl';
import { FormData as CreateOrganizationFormType } from './CreateEnterPriseAccountForm';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { errorMessage } from '@services';

interface CreateEnterpriseAccountModalProps {
  onClose: () => void;
  open: boolean;
  companyType: CompanyType;
  isBusiness?: boolean;
}

export const CreateEnterpriseAccountModal: FC<CreateEnterpriseAccountModalProps> = ({
  onClose,
  open,
  companyType,
}) => {
  const defaultValues: CreateOrganizationFormType = useMemo(
    () => ({
      name: '',
      subdomain: '',
      logo: '',
      plan: '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyType],
  );

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<CreateOrganizationFormType>({
    resolver: yupResolver(createEnterpriseAccountValidation),
    defaultValues,
    mode: 'onChange',
    delayError: 700,
  });

  const values = watch();
  const isDirty = !isEqual(values, defaultValues);

  const { getPresignedUrl } = usePresignedUrl(
    '/admin/api/v1/organizations/logo/presigned-url',
    true,
  );

  const [isConfirmOpen, onOpenConfirm, onConfirmCancel, onSubmitConfirm] = useConfirmationModal(
    false,
    onClose,
  );
  const [isSuccessOpen, , openSuccessModal, closeSuccessModal] = useModalState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState<boolean>(false);

  const closeAllModal = () => {
    queryClient.refetchQueries('enterprise-organizations');
    closeSuccessModal();
    onClose();
  };

  const { mutate: createOrganizationMutation, isLoading } = useMutation(
    (data: CreateOrganizationFormType) =>
      ApiClientEnterprise.post('/admin/api/v1/organizations', data),
    {
      onSuccess: openSuccessModal,
      onError: (error: any) => {
        errorMessage(error.response?.data?.errorMessage);
      },
    },
  );

  const uploadLogoToS3 = async (logoFile: File): Promise<string | null> => {
    try {
      setIsUploadingLogo(true);
      const presignedData = await getPresignedUrl();
      if (!presignedData) {
        return null;
      }
      const fileData = new Uint8Array(await logoFile.arrayBuffer());
      await axios.put(presignedData.presignedUrl, fileData, {
        headers: { 'Content-Type': logoFile.type },
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      });

      return presignedData.imageUrl;
    } catch (error) {
      console.error('Error uploading logo to S3:', error);
      return null;
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const onFinalSubmit = async (data: CreateOrganizationFormType) => {
    const uploadData = { ...data };

    if (data.logo && data.logoFile) {
      const resultStatus = await uploadLogoToS3(data.logoFile);

      if (!resultStatus) {
        console.error('Failed to upload logo to S3');
        return;
      }

      uploadData.logo = resultStatus as string;
    }

    delete uploadData.logoFile;
    // TODO: temporarily remove plan field
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { plan, ...restData } = uploadData;
    createOrganizationMutation(restData);
  };

  return (
    <>
      <MainModal
        open={open}
        isConfirmOpen={isConfirmOpen}
        isSuccessOpen={isSuccessOpen}
        isDirty={isDirty}
        isValid={isValid}
        isLoading={isUploadingLogo || isLoading}
        control={control}
        values={values}
        setValue={setValue}
        handleSubmit={handleSubmit}
        onClose={onClose}
        onOpenConfirm={onOpenConfirm}
        onFinalSubmit={onFinalSubmit}
      />
      <ConfirmModal
        open={isConfirmOpen}
        onCancel={onConfirmCancel}
        onSubmit={onSubmitConfirm}
        title="Are you sure you want to close modal?"
        submitText="Yes, close"
      />
      <SuccessModal open={isSuccessOpen} onClose={closeAllModal} />
    </>
  );
};
